"use client"

/*
========================================
PRODUCT PREVIEW

BLOCK: MAIN IMAGE

Отвечает за:
- центральную большую картинку товара
- отображение активной картинки
- контейнер изображения
- открытие встроенного полноэкранного просмотра
- листание всех фото в просмотре

Не содержит:
- мини-картинки
- шторки
- цену
- текст

Анимация подключается отдельно
из product-preview-animations
========================================
*/

import Image from "next/image"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"

type ProductPreviewMainImageProps = {
  activeImage: string
  gallery: { url: string | null }[]
  activeIndex: number
  setActiveIndex: (index: number) => void
  title?: string | null
  isOpen: boolean
  setIsOpen: (value: boolean) => void
}

export default function ProductPreviewMainImage({
  activeImage,
  gallery,
  activeIndex,
  setActiveIndex,
  title,
  isOpen,
  setIsOpen,
}: ProductPreviewMainImageProps) {
  const [modalIndex, setModalIndex] = useState(activeIndex)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    setModalIndex(activeIndex)
  }, [activeIndex, isOpen])

  const goNext = () => {
    if (!gallery.length) return
    const nextIndex = (modalIndex + 1) % gallery.length
    setModalIndex(nextIndex)
    setActiveIndex(nextIndex)
  }

  const goPrev = () => {
    if (!gallery.length) return
    const prevIndex = (modalIndex - 1 + gallery.length) % gallery.length
    setModalIndex(prevIndex)
    setActiveIndex(prevIndex)
  }

  const restoreScroll = () => {
    document.documentElement.style.overflow = ""
    document.body.style.overflow = ""
    document.documentElement.style.touchAction = ""
    document.body.style.touchAction = ""
    document.documentElement.style.position = ""
    document.body.style.position = ""
    document.documentElement.style.width = ""
    document.body.style.width = ""
  }

  useEffect(() => {
    if (!isOpen) {
      restoreScroll()
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false)
      }

      if (e.key === "ArrowRight") {
        goNext()
      }

      if (e.key === "ArrowLeft") {
        goPrev()
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    document.documentElement.style.overflow = "hidden"
    document.body.style.overflow = "hidden"
    document.documentElement.style.touchAction = "none"
    document.body.style.touchAction = "none"

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      restoreScroll()
    }
  }, [isOpen, modalIndex, gallery.length, setIsOpen])

  const openModal = () => {
    setModalIndex(activeIndex)
    setIsOpen(true)
  }

  const closeModal = () => {
    setIsOpen(false)
    restoreScroll()
  }

  const modalImage = gallery[modalIndex]?.url || activeImage

  const modalContent =
    isOpen && mounted
      ? createPortal(
          <div
            className="
              fixed inset-0 z-[99999]
              flex items-center justify-center
              bg-black/92 md:bg-black/92
              backdrop-blur-md
              px-4 py-6
            "
            onDoubleClick={closeModal}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                closeModal()
              }}
              className="
                absolute right-4 top-4 z-[100002]
                flex h-11 w-11 items-center justify-center
                rounded-full
                bg-white/92
                text-2xl leading-none text-neutral-900
                shadow-[0_8px_20px_rgba(0,0,0,0.25)]
                transition hover:scale-105
              "
              aria-label="Close image preview"
            >
              ×
            </button>

            {gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    goPrev()
                  }}
                  className="
                    absolute left-4 top-1/2 z-[100002]
                    -translate-y-1/2
                    flex h-12 w-12 items-center justify-center
                    rounded-full
                    bg-white/88
                    text-2xl text-neutral-900
                    shadow-[0_10px_24px_rgba(0,0,0,0.30)]
                    transition hover:scale-105
                  "
                  aria-label="Previous image"
                >
                  ‹
                </button>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    goNext()
                  }}
                  className="
                    absolute right-4 top-1/2 z-[100002]
                    -translate-y-1/2
                    flex h-12 w-12 items-center justify-center
                    rounded-full
                    bg-white/88
                    text-2xl text-neutral-900
                    shadow-[0_10px_24px_rgba(0,0,0,0.30)]
                    transition hover:scale-105
                  "
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}

            <div
              className="
                relative z-[100001]
                w-full max-w-[1100px]
                overflow-hidden rounded-3xl
                border border-white/20
                bg-black/30 md:bg-black/30
                shadow-[0_20px_60px_rgba(0,0,0,0.45)]
              "
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={closeModal}
            >
              <div className="relative aspect-[4/5] max-h-[92vh] w-full bg-transparent">
                {modalImage && (
                  <Image
                    src={modalImage}
                    alt={title || "Product image"}
                    fill
                    className="object-contain"
                    priority
                    sizes="100vw"
                  />
                )}
              </div>
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <>
      <div
        className="
          relative
          block
          aspect-[4/5]
          w-full
          overflow-hidden
          rounded-2xl
          bg-neutral-100
          border border-white/90
          shadow-[0_4px_8px_rgba(0,0,0,0.6),0_6px_10px_rgba(0,0,0,0.5),0_4px_4px_rgba(0,0,0,0.35)]
          hover:shadow-[0_8px_15px_rgba(0,0,0,0.4),0_10px_16px_rgba(0,0,0,0.25)]
          transition-transform
          duration-300
          hover:scale-[1.015]
          text-left
          select-none
        "
      >
        <div className="pointer-events-none absolute inset-0 z-[2] rounded-[inherit] border border-white/75" />

        <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-16 bg-gradient-to-b from-white/18 via-white/6 to-transparent" />

        {activeImage && (
          <div
            className="absolute inset-0 will-change-transform"
            style={{ animation: "floatOrganic 6.5s ease-in-out infinite" }}
          >
            <Image
              src={activeImage}
              alt={title || "Product image"}
              fill
              className="
                object-cover
                object-center
                scale-[1.03]
                transition-transform
                duration-[6500ms]
              "
              priority={false}
              sizes="(max-width: 640px) 100vw, 25vw"
            />
          </div>
        )}

        {/* 
        ========================================
        HOT ZONE FOR DOUBLE CLICK

        Важно:
        - модалка открывается не по всей картинке
        - слева и справа оставляем свободные зоны под шторки
        - примерно по 44px с каждой стороны
        - на desktop чуть шире
        ========================================
        */}
        <button
          type="button"
          onDoubleClick={openModal}
          className="
            absolute top-0 bottom-0 left-[44px] right-[44px]
            sm:left-[52px] sm:right-[52px]
            z-[5]
            cursor-zoom-in
            bg-transparent
          "
          aria-label="Open full image preview"
        />
      </div>

      {modalContent}
    </>
  )
}